import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { TourOverlay } from './TourOverlay';

export interface TourStep {
  id: string;
  target: string; // CSS selector
  title: string;
  body: string;
  placement: 'top' | 'bottom' | 'left' | 'right';
  spotlightShape: 'rect' | 'circle';
  /**
   * Runs before the step is measured — e.g. switch a hash-routed tab so the
   * `target` is on screen. The overlay re-measures for a few frames after
   * calling it, so the target may appear on the next render.
   */
  before?: () => void;
}

export interface TourState {
  tourId: string;
  steps: TourStep[];
  currentIndex: number;
}

interface TourContextValue {
  state: TourState | null;
  startTour: (tourId: string, steps: TourStep[]) => void;
  stopTour: () => void;
  nextStep: () => void;
  prevStep: () => void;
}

const TourContext = createContext<TourContextValue | null>(null);

export function useTour(): TourContextValue {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTour must be used inside TourProvider');
  return ctx;
}

export function TourProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<TourState | null>(null);

  const startTour = useCallback((tourId: string, steps: TourStep[]) => {
    if (steps.length === 0) return;
    setState({ tourId, steps, currentIndex: 0 });
  }, []);

  const stopTour = useCallback(() => setState(null), []);

  const nextStep = useCallback(() => {
    setState((s) => {
      if (!s) return null;
      const next = s.currentIndex + 1;
      if (next >= s.steps.length) return null;
      return { ...s, currentIndex: next };
    });
  }, []);

  const prevStep = useCallback(() => {
    setState((s) => {
      if (!s || s.currentIndex === 0) return s;
      return { ...s, currentIndex: s.currentIndex - 1 };
    });
  }, []);

  useEffect(() => {
    if (!state) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') stopTour();
      if (e.key === 'ArrowRight') nextStep();
      if (e.key === 'ArrowLeft') prevStep();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state, stopTour, nextStep, prevStep]);

  // inject pulse animation once
  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'compass-tour-styles';
    style.textContent = `
      @keyframes tour-pulse {
        0%   { box-shadow: 0 0 0 0 var(--accent, #6366f1); opacity: 1; }
        70%  { box-shadow: 0 0 0 12px transparent; opacity: 0.3; }
        100% { box-shadow: 0 0 0 0 transparent; opacity: 0; }
      }
    `;
    if (!document.getElementById('compass-tour-styles')) {
      document.head.appendChild(style);
    }
    return () => style.remove();
  }, []);

  return (
    <TourContext.Provider value={{ state, startTour, stopTour, nextStep, prevStep }}>
      {children}
      {state && <TourOverlay state={state} onNext={nextStep} onPrev={prevStep} onClose={stopTour} />}
    </TourContext.Provider>
  );
}
